import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Info, X, Cpu, FileText } from 'lucide-react';

const WidgetInfoPopover = ({ agents, prompt }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);

  // Recalculate position every time it opens
  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target)) {
        const panel = document.getElementById('widget-info-panel');
        if (panel && panel.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const popover = open
    ? ReactDOM.createPortal(
        <div
          id="widget-info-panel"
          style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 9999, width: 320 }}
          className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-700">Widget Details</span>
            <button
              onClick={() => setOpen(false)}
              className="p-0.5 hover:bg-gray-200 rounded text-gray-500 transition-colors"
            >
              <X size={12} />
            </button>
          </div>
          <div className="p-3 space-y-3">
            {/* Agents */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Cpu size={12} style={{ color: '#000080' }} />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Agents Used</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {agents.map((agent, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded-full font-medium border" style={{ backgroundColor: 'rgba(0,0,128,0.07)', color: '#000080', borderColor: 'rgba(0,0,128,0.15)' }}>
                    {agent}
                  </span>
                ))}
              </div>
            </div>
            {/* Prompt */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <FileText size={12} style={{ color: '#000080' }} />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Prompt Template</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 max-h-52 overflow-y-auto">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono leading-relaxed">{prompt}</pre>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg hover:bg-white/70 text-gray-400 hover:text-gray-600 transition-colors"
        title="Widget info"
        aria-label="Widget information"
      >
        <Info size={14} />
      </button>
      {popover}
    </>
  );
};

export default WidgetInfoPopover;
